/**
 * SlotsCard Component
 * 시간대별 배달 슬롯 카드
 */
import React, { useState, useEffect } from 'react';
import styles from '../../styles/shared/SlotsCard.module.css';

interface SlotData {
    id: string;
    icon: string;
    name: string;
    count: number;
    tooltipTitle: string;
    tooltipContent: string;
}

interface SlotsCardProps {
    morningLunchPeakCount: number;
    afternoonNonPeakCount: number;
    eveningPeakCount: number;
    nightPeakCount: number;
    onSlotClick?: (slotId: string) => void;
    clickable?: boolean;
}

export const SlotsCard: React.FC<SlotsCardProps> = ({
    morningLunchPeakCount,
    afternoonNonPeakCount,
    eveningPeakCount,
    nightPeakCount,
    onSlotClick,
    clickable = false
}) => {
    const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

    const slotData: SlotData[] = [
        {
            id: 'morning-lunch-peak',
            icon: '🌅',
            name: '아침/점심 피크',
            count: morningLunchPeakCount,
            tooltipTitle: '아침/점심 피크',
            tooltipContent: '평일: 06:00 ~ 12:59\n주말: 06:00 ~ 13:59'
        },
        {
            id: 'afternoon-non-peak',
            icon: '☀️',
            name: '오후 논피크',
            count: afternoonNonPeakCount,
            tooltipTitle: '오후 논피크',
            tooltipContent: '평일: 13:00 ~ 16:59\n주말: 14:00 ~ 16:59'
        },
        {
            id: 'evening-peak',
            icon: '🌆',
            name: '저녁 피크',
            count: eveningPeakCount,
            tooltipTitle: '저녁 피크',
            tooltipContent: '평일: 17:00 ~ 19:59\n평일/주말 동일'
        },
        {
            id: 'night-peak',
            icon: '🌙',
            name: '야간/심야',
            count: nightPeakCount,
            tooltipTitle: '야간/심야 피크',
            tooltipContent: '평일: 20:00 ~ 02:59\n평일/주말 동일'
        }
    ];

    const toggleTooltip = (event: React.MouseEvent, tooltipId: string) => {
        event.stopPropagation();
        setActiveTooltip(activeTooltip === tooltipId ? null : tooltipId);
    };

    const handleSlotClick = (slotId: string) => {
        if (clickable && onSlotClick) {
            onSlotClick(slotId);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (!target.closest(`.${styles['info-btn']}`) && !target.closest(`.${styles['tooltip']}`)) {
                setActiveTooltip(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    return (
        <div className={styles['slots-card']}>
            <h2 className={styles['slots-title']}>
                <span className={styles['slots-title-icon']}>📊</span>
                시간대별 배달 현황
            </h2>
            <div className={styles['slots-list']}>
                {slotData.map((slot) => (
                    <div
                        key={slot.id}
                        className={`${styles['slot-item']} ${activeTooltip === slot.id ? styles['slot-item-active'] : ''} ${clickable ? styles['slot-item-clickable'] : ''}`}
                        onClick={() => handleSlotClick(slot.id)}
                    >
                        <div className={styles['slot-header']}>
                            <span className={styles['slot-icon']}>{slot.icon}</span>
                            <span className={styles['slot-name']}>{slot.name}</span>
                            <button
                                className={styles['info-btn']}
                                onClick={(e) => toggleTooltip(e, slot.id)}
                            >
                                <i className="fas fa-question-circle"></i>
                            </button>
                            <div className={`${styles['tooltip']} ${slot.id === 'night-peak' ? styles['tooltip-top'] : ''} ${activeTooltip === slot.id ? styles['active'] : ''}`}>
                                <div className={styles['tooltip-title']}>{slot.tooltipTitle}</div>
                                <div className={styles['tooltip-content']}>
                                    {slot.tooltipContent.split('\n').map((line, idx) => (
                                        <React.Fragment key={idx}>
                                            {line}
                                            {idx < slot.tooltipContent.split('\n').length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className={styles['slot-count']}>{slot.count}건</div>
                    </div>
                ))}
            </div>
        </div>
    );
};
